<template>
  <section class="coaching-page">
    <header class="section-header">
      <div class="section-heading">
        <v-btn icon class="back-action academy-local-back" aria-label="Zurück zu Leadership" to="/akademie/leadership"
          ><v-icon>mdi-arrow-left</v-icon></v-btn
        >
        <span class="section-icon"><v-icon>mdi-school-outline</v-icon></span>
        <div>
          <div class="eyebrow">Entwickle deine Führungskompetenz</div>
          <h2>Leadership Coaching</h2>
          <p>Wähle ein Thema und arbeite dich durch Tools, Lehrvideos und die abschliessende Evaluation.</p>
        </div>
      </div>
    </header>

    <div v-if="loading" class="module-grid">
      <v-skeleton-loader v-for="index in 4" :key="index" dark type="image, article, actions" class="module-skeleton" />
    </div>

    <div v-else-if="modules.length" class="module-grid">
      <article
        v-for="moduleItem in modules"
        :key="moduleItem._id"
        class="module-card"
        tabindex="0"
        role="button"
        @click="openModule(moduleItem)"
        @keydown.enter="openModule(moduleItem)"
        @keydown.space.prevent="openModule(moduleItem)">
        <div class="card-top">
          <span class="module-icon"
            ><v-icon>{{ moduleItem.icon || "mdi-account-tie-outline" }}</v-icon></span
          >
          <span class="evaluation-badge"
            ><v-icon>{{ evaluationIcon(moduleItem) }}</v-icon
            >{{ evaluationLabel(moduleItem) }}</span
          >
        </div>
        <div class="module-copy">
          <span>{{ moduleItem.topic || "Leadership Coaching" }}</span>
          <h3>{{ moduleItem.title }}</h3>
          <p>{{ moduleItem.description || "Praxisnahes Wissen und Werkzeuge für deinen Führungsalltag." }}</p>
        </div>
        <div class="module-facts">
          <span><v-icon>mdi-play-circle-outline</v-icon>{{ (moduleItem.lessons && moduleItem.lessons.length) || 0 }} Lektionen</span>
          <span><v-icon>mdi-clock-outline</v-icon>{{ moduleItem.estimatedMinutes || 0 }} Min</span>
          <span><v-icon>mdi-toolbox-outline</v-icon>{{ (moduleItem.toolbox && moduleItem.toolbox.length) || 0 }} Tools</span>
        </div>
        <div class="progress-copy">
          <span>Fortschritt</span><strong>{{ progressFor(moduleItem) }}%</strong>
        </div>
        <v-progress-linear :value="progressFor(moduleItem)" color="#009640" background-color="rgba(255,255,255,0.08)" height="7" rounded />
        <div class="card-action">Modul öffnen <v-icon>mdi-arrow-right</v-icon></div>
      </article>
    </div>

    <div v-else class="empty-state">
      <span><v-icon>mdi-bookshelf</v-icon></span>
      <h3>Noch keine Leadership-Module</h3>
      <p>Sobald die Academy-Leitung Inhalte freischaltet, erscheinen sie hier.</p>
    </div>
  </section>
</template>

<script>
import api from "@/api"

export default {
  name: "LeadershipCoaching",
  data() {
    return { modules: [], progressEntries: [], loading: false }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [modules, progressEntries] = await Promise.all([
          api.akademie.fetchLeadershipModules(),
          api.akademie.fetchProgress({ track: "leadership" }),
        ])
        this.modules = (modules || [])
          .filter((moduleItem) => moduleItem.active !== false && (!moduleItem.track || moduleItem.track === "leadership"))
          .sort((first, second) => (first.order || 0) - (second.order || 0))
        this.progressEntries = progressEntries || []
      } catch (error) {
        console.error("Leadership Coaching konnte nicht geladen werden", error)
      } finally {
        this.loading = false
      }
    },
    evaluationType(moduleItem) {
      return (moduleItem.evaluation && moduleItem.evaluation.type) || moduleItem.evaluationType || (moduleItem.quiz ? "quiz" : "none")
    },
    evaluationLabel(moduleItem) {
      const labels = {
        quiz: "Wissenstest",
        video: "Video-Abgabe",
        video_submission: "Video-Abgabe",
        document_submission: "Praxisaufgabe",
        acknowledgement: "Bestätigung ohne Test",
        none: "Ohne Evaluation",
      }
      return labels[this.evaluationType(moduleItem)] || "Evaluation"
    },
    evaluationIcon(moduleItem) {
      const type = this.evaluationType(moduleItem)
      if (type === "acknowledgement") return "mdi-check-decagram-outline"
      if (type === "document_submission") return "mdi-file-document-edit-outline"
      return ["video", "video_submission"].includes(type) ? "mdi-video-account" : "mdi-help-circle-outline"
    },
    progressFor(moduleItem) {
      const progress = this.progressEntries.find((entry) => entry.module === moduleItem._id || (entry.module && entry.module._id === moduleItem._id))
      if (!progress) return 0
      if (progress.completedAt) return 100
      const lessons = moduleItem.lessons || []
      const activeLessonIds = new Set(lessons.map((lesson) => String(lesson && (lesson._id || lesson))))
      const completedLessons = (progress.lessons || []).filter((lesson) => {
        const lessonId = lesson.lesson && (lesson.lesson._id || lesson.lesson)
        return lesson.completedAt && activeLessonIds.has(String(lessonId))
      }).length
      const evaluationType = this.evaluationType(moduleItem)
      const evaluationSteps = evaluationType === "none" ? 0 : 1
      const evaluationCompleted = Boolean(progress.evaluationType === evaluationType && progress.evaluationCompletedAt)
      const completedSteps = completedLessons + (evaluationSteps && evaluationCompleted ? 1 : 0)
      const totalSteps = lessons.length + evaluationSteps
      return totalSteps ? Math.floor((completedSteps / totalSteps) * 100) : 0
    },
    openModule(moduleItem) {
      this.$router.push({ path: `/akademie/modules/${moduleItem._id}`, query: { from: "leadership" } })
    },
  },
}
</script>

<style scoped>
.coaching-page {
  --border: rgba(255, 255, 255, 0.1);
  --muted: #a3a3a8;
  color: #f4f4f5;
  font-size: 1rem;
}

.section-header {
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.035);
}

.section-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}

.back-action,
.section-icon {
  display: grid;
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  place-items: center;
  border: 1px solid var(--border);
  border-radius: 0.875rem !important;
  color: #fff !important;
  background: rgba(255, 255, 255, 0.05) !important;
}

.section-icon {
  color: #93c5fd !important;
  background: rgba(59, 130, 246, 0.13) !important;
}

.section-icon .v-icon {
  color: currentColor;
}

.eyebrow,
.module-copy > span {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.section-heading h2 {
  margin: 0.1875rem 0 0.375rem;
  color: #fff;
  font-size: 1.5rem;
}

.section-heading p {
  margin: 0;
  color: #c7c7cb;
  font-size: 1rem;
  line-height: 1.5;
}

.module-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.module-skeleton,
.module-card {
  border: 1px solid var(--border);
  border-radius: 1.25rem;
}

.module-card {
  display: flex;
  min-width: 0;
  min-height: 25rem;
  flex-direction: column;
  padding: 1.125rem;
  outline: none;
  background: radial-gradient(circle at 100% 0, rgba(59, 130, 246, 0.1), transparent 12rem), rgba(255, 255, 255, 0.035);
  cursor: pointer;
}

.module-card:focus-visible {
  border-color: #7ee5a6;
}

.card-top,
.progress-copy,
.module-facts,
.card-action {
  display: flex;
  align-items: center;
}

.card-top {
  justify-content: space-between;
  gap: 0.75rem;
}

.module-icon {
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border-radius: 0.9375rem;
  color: #93c5fd;
  background: rgba(59, 130, 246, 0.13);
}

.module-icon .v-icon {
  color: currentColor;
}

.evaluation-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.75rem;
  font-weight: 700;
}

.evaluation-badge .v-icon {
  color: #7ee5a6;
  font-size: 1rem;
}

.module-copy {
  margin: 1rem 0;
}

.module-copy h3 {
  margin: 0.25rem 0 0.5rem;
  color: #fff;
  font-size: 1.25rem;
}

.module-copy p {
  margin: 0;
  color: #b8b8bd;
  font-size: 0.875rem;
  line-height: 1.55;
}

.module-facts {
  flex-wrap: wrap;
  gap: 0.5rem 0.875rem;
  margin-bottom: auto;
  color: var(--muted);
  font-size: 0.75rem;
}

.module-facts span {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.module-facts .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.progress-copy {
  justify-content: space-between;
  margin: 1rem 0 0.375rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.progress-copy strong {
  color: #7ee5a6;
}

.card-action {
  justify-content: space-between;
  margin-top: 1rem;
  color: #d4d4d8;
  font-size: 0.875rem;
  font-weight: 750;
}

.card-action .v-icon {
  color: #7ee5a6;
}

.empty-state {
  display: grid;
  min-height: 18rem;
  justify-items: center;
  align-content: center;
  margin-top: 1rem;
  padding: 1.25rem;
  border: 1px dashed rgba(255, 255, 255, 0.14);
  border-radius: 1.25rem;
  color: var(--muted);
  text-align: center;
}

.empty-state > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  place-items: center;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.05);
}

.empty-state h3 {
  margin: 0.875rem 0 0.375rem;
  color: #fff;
  font-size: 1.25rem;
}

.empty-state p {
  margin: 0;
  font-size: 0.875rem;
}

@media (max-width: 68rem) {
  .module-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 43rem) {
  .section-heading {
    flex-wrap: wrap;
  }

  .section-heading > div:last-child {
    width: 100%;
  }

  .module-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .module-card {
    min-height: 0;
  }
}
</style>
